import { Group, Select, SimpleGrid, TextInput } from '@mantine/core';
import { useMemo } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { ServerValidatedZodForm } from '@apple/ui/form/components/ValidatedForm/ServerValidatedZodForm';
import type {
	CountryOption,
	CurrencyOption,
	LanguageOption,
	OptionKeyValue,
	TimeZoneOption,
} from '@apple/utils/globalization/models';

import type { ProfileDto, UpdateProfileRequest } from '@apple/features/auth/models/user';
import { updateProfileSchema } from '@apple/features/auth/models/user';

export type ProfileProps = {
	profile: ProfileDto;
	timeZones: TimeZoneOption[];
	languages?: LanguageOption[];
	currencies?: CurrencyOption[];
	countries: CountryOption[];
	onSubmit: (request: UpdateProfileRequest) => Promise<void>;
};

export type ProfileFormValues = {
	username: string;
	firstname: string;
	lastname: string;
	email: string;
	timezone: string;
	language?: string;
	country: string;
	currency?: string;
};

const getOptionKey = (options: OptionKeyValue[], currentValue: string) => {
	return options.some(x => x.key === currentValue) ? currentValue : (options[0]?.key ?? '');
};

export function ProfileForm({
	profile,
	countries,
	currencies,
	languages,
	timeZones,
	onSubmit,
}: ProfileProps) {
	const { t } = useTranslation('profile');

	async function handleSubmit(data: ProfileFormValues) {
		const request = {
			userName: data.username,
			firstName: data.firstname,
			lastName: data.lastname,
			emailAddress: data.email,
			language: data.language ? data.language : undefined,
			timeZone: data.timezone,
			numberFormatCountry: data.country,
			currencyIsoCode: data.currency ? data.currency : undefined,
		} as UpdateProfileRequest;

		await onSubmit(request);
	}

	const initialValues: ProfileFormValues = useMemo(
		() => ({
			username: profile.userName,
			firstname: profile.firstName,
			lastname: profile.lastName,
			email: profile.emailAddress,
			timezone: getOptionKey(timeZones, profile.timeZone),
			language: languages ? getOptionKey(languages, profile.language) : undefined,
			country: getOptionKey(countries, profile.numberFormatCountry),
			currency: currencies ? getOptionKey(currencies ?? [], profile.currencyIsoCode) : '',
		}),
		[
			countries,
			currencies,
			languages,
			profile.currencyIsoCode,
			profile.emailAddress,
			profile.firstName,
			profile.language,
			profile.lastName,
			profile.numberFormatCountry,
			profile.timeZone,
			profile.userName,
			timeZones,
		],
	);

	const timeZoneOptions = timeZones.map(option => ({
		value: option.key,
		label: option.value,
	}));
	const languageOptions = languages?.map(option => ({
		value: option.key,
		label: option.value,
	}));
	const currencyOptions = currencies?.map(option => ({
		value: option.key,
		label: option.value,
	}));
	const countryOptions = countries.map(option => ({
		value: option.key,
		label: option.value,
	}));

	return (
		<SimpleGrid cols={{ base: 1, md: 2 }}>
			<ServerValidatedZodForm
				name='profile-form'
				validationType='ProfileSettings'
				schema={updateProfileSchema}
				initialValues={initialValues}
				schemaExtension={schema => {
					return schema.refine(
						data => data.firstname.length + data.lastname.length < 35,
						{
							path: ['firstname'],
							message: t('profile.nameTooLong'),
						},
					);
				}}
				fallbackErrorMessage={t('profile.save.fail.message')}
				onValidSubmit={handleSubmit}
			>
				{({ form, renderSubmitButton }) => (
					<>
						<TextInput
							{...form.getInputProps('username')}
							label={t('profile.userName')}
							value={profile.userName}
							readOnly
							disabled
							mt='md'
							data-testid='username'
						/>
						<TextInput
							label={t('profile.userRole')}
							value={profile.roles.length > 0 ? profile.roles[0] : ''}
							readOnly
							disabled
							mt='sm'
							data-testid='role'
						/>
						<TextInput
							{...form.getInputProps('firstname')}
							label={t('profile.firstName')}
							placeholder={t('profile.inputPlaceholder')}
							required
							mt='sm'
							data-testid='firstname'
						/>
						<TextInput
							{...form.getInputProps('lastname')}
							label={t('profile.lastName')}
							placeholder={t('profile.inputPlaceholder')}
							required
							mt='sm'
							data-testid='lastname'
						/>
						<TextInput
							{...form.getInputProps('email')}
							label={t('profile.emailAddress')}
							placeholder={t('profile.inputPlaceholder')}
							required
							mt='sm'
							data-testid='email'
						/>
						<Select
							data={timeZoneOptions}
							label={t('profile.timeZone')}
							value={form.values.timezone}
							onChange={(value: string | null) =>
								value && form.setFieldValue('timezone', value)
							}
							required
							mt='sm'
						/>
						{languageOptions && (
							<Select
								data={languageOptions}
								label={t('profile.language')}
								value={form.values.language ?? 'en'}
								onChange={(value: string | null) =>
									value &&
									form.setFieldValue(
										'language',
										form.values.language == '' ? undefined : value,
									)
								}
								required
								mt='sm'
							/>
						)}
						<Select
							data={countryOptions}
							label={t('profile.country')}
							value={form.values.country}
							onChange={(value: string | null) =>
								value && form.setFieldValue('country', value)
							}
							required
							mt='sm'
						/>
						{currencyOptions && (
							<Select
								data={currencyOptions}
								label={t('profile.currency')}
								value={form.values.currency}
								onChange={(value: string | null) =>
									value &&
									form.setFieldValue(
										'currency',
										form.values.language == undefined ? '' : value,
									)
								}
								required
								mt='sm'
							/>
						)}
						<Group justify='flex-end' mt='md'>
							{renderSubmitButton({
								text: t('profile.save.saveButton'),
								fullWidth: false,
								mt: 'lg',
								disableIfInvalid: true,
								disableIfNotDirty: true,
							})}
						</Group>
					</>
				)}
			</ServerValidatedZodForm>
		</SimpleGrid>
	);
}
